import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../../../styles/components/AccountActionModal.module.css";
import BaseInput from "../../../ui/inputs/BaseInput/BaseInput";
import Button from "../../../ui/buttons/Button";
import { ReactComponent as EyeClosed } from "../../../assets/icons/eye-closed.svg";
import { ReactComponent as EyeOpened } from "../../../assets/icons/eye-opened.svg";
import { useOverlayClose } from "../../../hooks/useOverlayClose";

const AccountActionModal = ({
  isOpen = false,
  title = "",
  description = null,
  fields = [],
  submitText = "Guardar",
  onCancel,
  onSubmit,
}) => {
  const initialValues = useMemo(() => {
    const values = {};
    fields.forEach((field) => {
      values[field.name] = field.value ?? "";
    });
    return values;
  }, [fields]);

  const [formValues, setFormValues] = useState(initialValues);
  const [showPasswords, setShowPasswords] = useState({});
  const [errors, setErrors] = useState({});
  const [fieldValidity, setFieldValidity] = useState({});

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setFormValues(initialValues);
      setShowPasswords({});
      setErrors({});
      setFieldValidity({});
    }

    wasOpenRef.current = isOpen;
  }, [isOpen, initialValues]);

  const overlayCloseProps = useOverlayClose(onCancel);

  if (!isOpen) return null;

  const handleChange = (name, value) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const togglePassword = (name) => {
    setShowPasswords((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const validate = () => {
    const newErrors = {};

    fields.forEach((field) => {
      if (field.required) {
        const value = formValues[field.name];

        if (!value || value.trim() === "") {
          newErrors[field.name] =
            field.requiredMessage || "Este campo es obligatorio";
        }
      }
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    let newValidity = {};

    if (!validate()) return;

    fields.forEach((field) => {
      const value = formValues[field.name];

      if (field.required && (!value || value.trim() === "")) {
        newValidity[field.name] = false;
      }
    });

    setFieldValidity((prev) => ({
      ...prev,
      ...newValidity,
    }));

    const hasInvalid = Object.values({
      ...fieldValidity,
      ...newValidity,
    }).some((v) => v === false);

    if (hasInvalid) return;

    onSubmit?.(formValues);
  };

  return (
    <div className={styles.overlay} {...overlayCloseProps}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>{title}</h3>
        </div>

        <div className={styles.body}>
          {description && <div className={styles.description}>{description}</div>}
          {fields.map((field) => {
            const isPassword = field.type === "password";
            const readOnly = !!field.readOnly;
            const inputType = isPassword
              ? showPasswords[field.name]
                ? "text"
                : "password"
              : field.type || "text";

            return (
              <div key={field.name} className={styles.fieldWrapper}>
                <BaseInput
                  label={field.label}
                  type={inputType}
                  value={formValues[field.name] ?? ""}
                  placeholder={field.placeholder || ""}
                  readOnly={readOnly}
                  error={errors[field.name]}
                  onChange={(value) => handleChange(field.name, value)}
                  rightIcon={
                    isPassword ? (
                      <button
                        type="button"
                        className={styles.eyeButton}
                        onClick={() => togglePassword(field.name)}
                        aria-label={
                          showPasswords[field.name]
                            ? "Ocultar contraseña"
                            : "Mostrar contraseña"
                        }
                      >
                        {showPasswords[field.name] ? <EyeOpened /> : <EyeClosed />}
                      </button>
                    ) : null
                  }
                  onValidation={(isValid) => {
                    setFieldValidity((prev) => ({
                      ...prev,
                      [field.name]: isValid,
                    }));
                  }}
                />
              </div>
            );
          })}
        </div>

        <div className={styles.footer}>

          <Button
              variant="secondary"
              tone="blue"
              onClick={onCancel}
            >
              Cancelar
            </Button>
          <Button
            variant="primary"
            tone="blue"
            onClick={handleSubmit}
          >
            {submitText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccountActionModal;
